// KeyInstructionsPopover — "eye" icon + stay-state modal with step-by-step
// instructions for creating a provider API key. iter-31-P2b.
//
// Unlike our InfoTip (hover, auto-closes), this one is:
//   - Click-to-open (never hover)
//   - Stays open until user clicks X or clicks outside OR presses Escape
//   - Contains clickable URLs that open in a new tab (target=_blank)
//   - Contains copy-to-clipboard buttons on example strings
//
// Drop it next to ANY key-entry input. Pass `keyType` to get the right copy.
//
// Currently supports keyType:
//   - "gemini_byok"  — Google AI Studio (Gemini) API key for AI calls
//   - "youtube"      — YouTube Data API v3 key for channel/video/comment pulls

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Eye, X, ArrowSquareOut, Copy, FloppyDisk, Question } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const INSTRUCTIONS = {
  gemini_byok: {
    title: "How to create your Gemini API key",
    intro: null,  // richer intro rendered inline below (Option C)
    steps: [
      {
        heading: "1 · Open Google AI Studio",
        body: "Google's official, free console for creating Gemini API keys. Sign in with a Google account. No billing required unless you exceed the free tier.",
        links: [
          { label: "Google AI Studio · aistudio.google.com", url: "https://aistudio.google.com/apikey" },
        ],
      },
      {
        heading: "2 · Click \"Create API key\"",
        body: "Top-right button. If Google asks which project to attach it to, pick \"Create API key in new project\" — that's the simplest path.",
      },
      {
        heading: "3 · Copy the generated key",
        body: "It looks like this:",
        copyExample: "AIzaSyXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
      },
      {
        heading: "4 · Paste it into the box next to this icon",
        body: "Click Save. You're done — every LLM call from your silos now bills your own Gemini account. You can remove or replace the key anytime.",
      },
    ],
    notes: [
      "Gemini keys start with \"AIza\" — same prefix as Google Maps/YouTube keys, but they're different products.",
      "The free tier is generous (thousands of calls/day). Most creators never hit the paid tier.",
      "Your key is encrypted at rest in My Silo's database (AES-128 + HMAC-SHA256) — a DB leak alone can't expose it.",
    ],
  },
  youtube: {
    title: "How to create your YouTube Data API v3 key",
    intro: "My Silo can pull titles, transcripts, comments, and entire channel video libraries from YouTube — but Google requires you to have your own YouTube Data API v3 key to do it. The key is free within quota.",
    steps: [
      {
        heading: "1 · Open Google Cloud Console",
        body: "If you don't already have a Cloud project, you'll create one here. This is different from Google AI Studio — YouTube uses the full Cloud Console.",
        links: [
          { label: "Google Cloud Console · console.cloud.google.com", url: "https://console.cloud.google.com/" },
        ],
      },
      {
        heading: "2 · Enable \"YouTube Data API v3\"",
        body: "In the search bar at the top, type YouTube Data API v3. Click it, then click Enable. Takes 5 seconds.",
        links: [
          { label: "Direct link to enable YouTube Data API v3", url: "https://console.cloud.google.com/apis/library/youtube.googleapis.com" },
        ],
      },
      {
        heading: "3 · Go to Credentials → Create API key",
        body: "Left sidebar → APIs & Services → Credentials. Top bar: + Create Credentials → API key. Google instantly generates one.",
        links: [
          { label: "Direct link to Credentials page", url: "https://console.cloud.google.com/apis/credentials" },
        ],
      },
      {
        heading: "4 · Copy the generated key",
        body: "It looks like this:",
        copyExample: "AIzaSyXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
      },
      {
        heading: "5 · (Optional but recommended) Restrict the key",
        body: "Click \"Edit API key\" → Under \"API restrictions\" → choose \"Restrict key\" → check only \"YouTube Data API v3\". This prevents the key from being abused if it leaks.",
      },
      {
        heading: "6 · Paste it into the YouTube key input",
        body: "Click Save. You can now pull YouTube transcripts, metadata, comments, and channel libraries straight into your silo.",
      },
    ],
    notes: [
      "YouTube Data API v3 gives every project 10,000 quota units/day free. One transcript pull costs ~1 unit. Plenty of headroom for most creators.",
      "YouTube keys start with \"AIza\" — same as Gemini keys but they are NOT interchangeable. Keep them separate.",
      "Your key is encrypted at rest in My Silo's database (AES-128 + HMAC-SHA256).",
    ],
  },
};

export default function KeyInstructionsPopover({ keyType, testid = "key-instructions", onSaved, variant = "eye", label = "How to get this key" }) {
  const [open, setOpen] = useState(false);
  const [candidate, setCandidate] = useState("");
  const [saving, setSaving] = useState(false);
  const panelRef = useRef(null);
  const cfg = INSTRUCTIONS[keyType];

  // Close on ESC. Click-outside handled via the backdrop layer.
  // Hook MUST come before any early return (rules-of-hooks).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset inline form whenever modal opens/closes.
  useEffect(() => {
    if (!open) {
      setCandidate(""); setSaving(false);
    }
  }, [open]);

  if (!cfg) return null;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Example copied — replace with your real key.");
    } catch { toast.error("Couldn't copy."); }
  };

  const saveEndpoint = keyType === "youtube" ? "/auth/youtube-key" : "/auth/byok";
  const saveFieldName = keyType === "youtube" ? "youtube_api_key" : "creator_api_key";

  const saveKey = async () => {
    if (!candidate.trim()) return;
    setSaving(true);
    try {
      const r = await api.patch(saveEndpoint, { [saveFieldName]: candidate.trim() });
      toast.success(`${keyType === "youtube" ? "YouTube" : "LLM"} key saved. Hit Test on the card to verify.`);
      onSaved?.(r.data);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save key.");
    } finally { setSaving(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && candidate.trim() && !saving) {
      e.preventDefault();
      saveKey();
    }
  };

  return (
    <>
      {variant === "button" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`How to create ${keyType} key`}
          data-testid={`${testid}-eye`}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#4A5D4E]/40 text-[#4A5D4E] bg-[#F0F1EE]/60 hover:bg-[#F0F1EE] hover:border-[#4A5D4E] transition-colors font-medium"
        >
          <Question size={12} weight="bold" /> {label}
        </button>
      ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`How to create ${keyType} key`}
        data-testid={`${testid}-eye`}
        className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE] transition-colors"
        title="How to create this key"
      >
        <Eye size={14} weight="duotone" />
      </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={`${testid}-modal`}>
          <div className="absolute inset-0 bg-[#1C1B1A]/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="relative z-10 w-full max-w-lg max-h-[85vh] bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex flex-col"
          >
            <div className="flex items-start justify-between px-6 py-4 border-b hairline bg-[#FDFCF8]">
              <div>
                <div className="font-serif-display text-lg text-[#1C1B1A]">{cfg.title}</div>
                <div className="text-xs text-[#6B6A68] mt-1">Click X or press Esc to close.</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-md text-[#6B6A68] hover:bg-white" data-testid={`${testid}-close`}>
                <X size={16} />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-5 space-y-5">
              {/* iter-31-P2c: rich intro — Option C copy for gemini_byok, simple intro for youtube. */}
              {keyType === "gemini_byok" ? (
                <div className="space-y-3" data-testid={`${testid}-intro`}>
                  <p className="text-sm text-[#1C1B1A] leading-relaxed">
                    Two ways to call the LLM: the <strong>platform key</strong> (default) or
                    {" "}<strong>your own Google key (BYOK)</strong>. Either works, either is tracked honestly —
                    platform credit you spend is <em>automatically reconciled when you cash out</em>,
                    so nothing "disappears." My Silo's <strong>MAXIMA logic</strong> always picks the best
                    result for the smallest cost, and you can tune power up when you want more muscle.
                  </p>
                  <div className="bg-[#F0F1EE] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E] mb-2">Three reasons creators add BYOK</div>
                    <ul className="text-xs text-[#1C1B1A] space-y-1.5">
                      <li className="flex items-start gap-2"><span className="text-[#4A5D4E] mt-0.5">·</span><span><strong>Cheaper long-term</strong> — calls bill Google at Google's price, no platform layer.</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#4A5D4E] mt-0.5">·</span><span><strong>Bigger cash payouts</strong> — no credit reserves tied up, more of your earnings convert to dollars when you withdraw.</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#4A5D4E] mt-0.5">·</span><span><strong>Your own quota</strong> — you tap Google's full free tier yourself (thousands of calls/day) instead of sharing platform quota.</span></li>
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#1C1B1A] leading-relaxed" data-testid={`${testid}-intro`}>{cfg.intro}</p>
              )}

              {/* Paste + Save (primary action) — moved to TOP per iter-31-P2c so
                  mobile users don't have to scroll past instructions. Test removed —
                  it lives on the BYOK/YT card itself for post-save validation. */}
              <div className="bg-[#FDFCF8] border-2 border-[#4A5D4E]/20 rounded-lg p-4" data-testid={`${testid}-inline-form`}>
                <div className="text-xs uppercase tracking-[0.2em] text-[#4A5D4E] mb-2 flex items-center gap-1.5">
                  <FloppyDisk size={12} weight="duotone" /> Paste your key here
                </div>
                <div className="flex gap-2 items-stretch">
                  <input
                    type="password"
                    value={candidate}
                    onChange={(e) => setCandidate(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Paste your key and press Enter"
                    data-testid={`${testid}-inline-input`}
                    autoComplete="off"
                    className="flex-1 bg-white border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                  />
                  <button
                    type="button"
                    onClick={saveKey}
                    disabled={!candidate.trim() || saving}
                    data-testid={`${testid}-inline-save`}
                    className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1"
                  >
                    <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
                  </button>
                </div>
                <div className="text-[10px] text-[#6B6A68] mt-2 italic">
                  After saving you can <strong>Test</strong> the key anytime from the card on your Operations tab. Press <kbd className="px-1 py-0.5 bg-white border hairline rounded">Enter</kbd> to save · <kbd className="px-1 py-0.5 bg-white border hairline rounded">Esc</kbd> to close.
                </div>
              </div>

              {/* Instructions — collapsed below the primary action. */}
              <div className="space-y-4">
                <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Need help getting a key?</div>
                {cfg.steps.map((step, i) => (
                  <div key={i} className="pb-4 border-b hairline last:border-0">
                    <div className="text-sm font-medium text-[#1C1B1A] mb-1.5">{step.heading}</div>
                    <div className="text-xs text-[#6B6A68] leading-relaxed">{step.body}</div>
                    {step.links && (
                      <div className="mt-2 space-y-1.5">
                        {step.links.map((lnk) => (
                          <a
                            key={lnk.url}
                            href={lnk.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`${testid}-link-${i}`}
                            className="inline-flex items-center gap-1.5 text-xs text-[#4A5D4E] hover:text-[#1C1B1A] hover:underline font-mono break-all"
                          >
                            <ArrowSquareOut size={12} weight="duotone" /> {lnk.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {step.copyExample && (
                      <div className="mt-2 flex items-center gap-2 bg-[#F0F1EE] rounded-md p-2">
                        <code className="text-[11px] font-mono text-[#1C1B1A] break-all flex-1 select-all">{step.copyExample}</code>
                        <button
                          onClick={() => copy(step.copyExample)}
                          data-testid={`${testid}-copy-${i}`}
                          className="p-1.5 rounded text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-white"
                          title="Copy example (replace with your real key)"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {cfg.notes.length > 0 && (
                <div className="bg-[#F0F1EE] rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1.5">Good to know</div>
                  <ul className="text-xs text-[#1C1B1A] space-y-1.5">
                    {cfg.notes.map((n, i) => <li key={i} className="flex items-start gap-2"><span className="text-[#4A5D4E] mt-0.5">·</span><span>{n}</span></li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
